import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import Swal from 'sweetalert2';
import { CibleTerrain, ObservationsService, PeseeEchantillon } from 'src/app/core/services/entities/observations.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { nouvelleClientRef } from './client-ref';
import { RoleUtil } from './role.util';

/**
 * Pesée d'échantillon (cohorte de volaille, cohorte de poissons, troupeau) : poids saisis un par un avec
 * moyenne, min-max et écart-type en direct, ou poids total + nombre ; résultat et évolution depuis la pesée
 * précédente ; historique. Effets côté serveur (pesée de volaille, poids moyen du suivi du bassin), annulés à la suppression.
 */
@Component({
  selector: 'bay-pesee-echantillon',
  template: `
    <section class="card border-0 shadow-sm" style="border-radius:20px;" aria-label="Pesée d'échantillon" data-etat="pesee-echantillon">
      <div class="card-body p-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0" style="color:#374151;">Pesée d'échantillon</h6>
          <button *ngIf="peutSaisir && !formulaire" type="button" class="btn btn-sm fw-semibold" style="background:#e5eadf;color:#0d631b;border-radius:10px;"
                  (click)="ouvrir()" data-test="nouvelle-pesee">
            <span class="material-symbols-outlined align-middle me-1" style="font-size:16px;" aria-hidden="true">scale</span>Peser un échantillon
          </button>
        </div>

        <form *ngIf="formulaire" class="mb-3 p-3 rounded-4" style="background:#f7fbf0;" (ngSubmit)="enregistrer()" data-etat="form-pesee">
          <div class="row g-2 mb-2 align-items-end">
            <div class="col-6 col-md-3">
              <label class="form-label small fw-semibold mb-1" [for]="id + '-date'">Date</label>
              <input [id]="id + '-date'" type="date" class="form-control form-control-sm" [(ngModel)]="date" name="date" [max]="aujourdhui" required>
            </div>
            <div class="col-6 col-md-3">
              <span class="form-label small fw-semibold mb-1 d-block">Unité</span>
              <div class="btn-group btn-group-sm" role="radiogroup" aria-label="Unité">
                <button *ngFor="let u of unites" type="button" class="btn" role="radio" [attr.aria-checked]="unite === u"
                        [class.btn-success]="unite === u" [class.btn-outline-secondary]="unite !== u" (click)="unite = u">{{ u }}</button>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="btn-group btn-group-sm" role="radiogroup" aria-label="Mode de saisie">
                <button type="button" class="btn" role="radio" [attr.aria-checked]="mode === 'un'" [class.btn-success]="mode === 'un'"
                        [class.btn-outline-secondary]="mode !== 'un'" (click)="mode = 'un'">Un par un</button>
                <button type="button" class="btn" role="radio" [attr.aria-checked]="mode === 'total'" [class.btn-success]="mode === 'total'"
                        [class.btn-outline-secondary]="mode !== 'total'" (click)="mode = 'total'">Total et nombre</button>
              </div>
            </div>
          </div>

          <ng-container *ngIf="mode === 'un'">
            <label class="form-label small fw-semibold mb-1" [for]="id + '-poids'">Poids d'un sujet ({{ unite }}) puis Entrée</label>
            <div class="d-flex gap-2 mb-2">
              <input [id]="id + '-poids'" type="number" inputmode="decimal" min="0" step="any" class="form-control form-control-sm" style="max-width:160px;"
                     [(ngModel)]="saisie" name="saisie" (keydown.enter)="$event.preventDefault(); ajouterPoids()" data-test="poids">
              <button type="button" class="btn btn-sm btn-outline-success" (click)="ajouterPoids()">Ajouter</button>
            </div>
            <div *ngIf="poids.length" class="d-flex flex-wrap gap-1 mb-2" aria-label="Poids saisis">
              <span *ngFor="let p of poids; let i = index" class="badge rounded-pill text-bg-light border">
                {{ p }} {{ unite }} <button type="button" class="btn btn-link btn-sm p-0 ms-1" [attr.aria-label]="'Retirer ' + p" (click)="poids.splice(i, 1)">×</button>
              </span>
            </div>
            <div *ngIf="poids.length" class="small mb-2" data-etat="stats-direct" aria-live="polite">
              {{ poids.length }} sujet(s) · moyenne <strong>{{ stats.moyenne | number:'1.0-2' }} {{ unite }}</strong>
              · min-max {{ stats.min | number:'1.0-2' }}–{{ stats.max | number:'1.0-2' }} · écart-type {{ stats.ecart | number:'1.0-2' }}
            </div>
          </ng-container>
          <div *ngIf="mode === 'total'" class="row g-2 mb-2">
            <div class="col-6 col-md-3">
              <label class="form-label small fw-semibold mb-1" [for]="id + '-total'">Poids total ({{ unite }})</label>
              <input [id]="id + '-total'" type="number" min="0" step="any" class="form-control form-control-sm" [(ngModel)]="poidsTotal" name="poidsTotal">
            </div>
            <div class="col-6 col-md-3">
              <label class="form-label small fw-semibold mb-1" [for]="id + '-nombre'">Nombre de sujets</label>
              <input [id]="id + '-nombre'" type="number" min="1" step="1" class="form-control form-control-sm" [(ngModel)]="nombre" name="nombre">
            </div>
            <div class="col-12 small" *ngIf="poidsTotal && nombre">Moyenne : <strong>{{ poidsTotal / nombre | number:'1.0-2' }} {{ unite }}</strong></div>
          </div>
          <div *ngIf="erreur" class="text-danger small mb-2" role="alert">{{ erreur }}</div>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-sm fw-semibold" style="background:#0d631b;color:#fff;border-radius:10px;" [disabled]="envoi">
              <span *ngIf="envoi" class="spinner-border spinner-border-sm me-1"></span>Enregistrer la pesée
            </button>
            <button type="button" class="btn btn-sm btn-light" (click)="formulaire = false">Annuler</button>
          </div>
        </form>

        <div *ngIf="resultat as r" class="alert alert-success rounded-4 small" role="status" data-etat="resultat-pesee">
          <div class="fw-bold mb-1">Moyenne : {{ g(r.moyenneG) }}<span *ngIf="r.minG != null"> · min-max {{ g(r.minG) }}–{{ g(r.maxG) }}</span></div>
          <div *ngIf="r.homogeneitePct != null">Homogénéité : {{ r.homogeneitePct | number:'1.0-0' }} %<span *ngIf="r.cvPct != null"> (CV {{ r.cvPct | number:'1.0-1' }} %)</span></div>
          <div *ngIf="r.evolutionG != null" data-etat="evolution">
            {{ r.evolutionG >= 0 ? '+' : '' }}{{ g(r.evolutionG) }} ({{ r.evolutionPct >= 0 ? '+' : '' }}{{ r.evolutionPct | number:'1.0-1' }} %)
            depuis le {{ r.precedenteDate | date:'dd/MM/yyyy' }}<span *ngIf="r.joursEcoules"> ({{ r.joursEcoules }} j)</span><span *ngIf="r.gainQuotidienG != null"> · {{ r.gainQuotidienG | number:'1.0-1' }} g/jour</span>
          </div>
          <div *ngIf="r.evolutionG == null" class="text-muted">Première pesée : l'évolution s'affichera à la prochaine.</div>
          <ul *ngIf="r.effets?.length" class="mb-0 mt-1 ps-3"><li *ngFor="let e of r.effets">{{ e }}</li></ul>
        </div>

        <p *ngIf="!chargement && !liste.length && !resultat" class="text-muted small mb-0">Aucune pesée d'échantillon pour l'instant.</p>
        <div *ngIf="liste.length" class="table-responsive">
          <table class="table table-sm small align-middle mb-0">
            <thead><tr><th>Date</th><th class="text-end">Sujets</th><th class="text-end">Moyenne</th><th class="text-end">Évolution</th><th>Par</th><th></th></tr></thead>
            <tbody>
              <tr *ngFor="let p of liste" [attr.data-pesee]="p.id">
                <td>{{ p.date | date:'dd/MM/yyyy' }}</td>
                <td class="text-end">{{ p.nombre ?? p.poidsG?.length }}</td>
                <td class="text-end fw-semibold">{{ g(p.moyenneG) }}</td>
                <td class="text-end" [class.text-success]="(p.evolutionG || 0) > 0" [class.text-danger]="(p.evolutionG || 0) < 0">
                  <ng-container *ngIf="p.evolutionG != null">{{ p.evolutionG >= 0 ? '+' : '' }}{{ g(p.evolutionG) }}</ng-container>
                </td>
                <td class="text-muted">{{ p.createdBy }}</td>
                <td class="text-end">
                  <button *ngIf="peutSupprimer(p)" type="button" class="btn btn-sm btn-link text-danger p-0" (click)="supprimer(p)" aria-label="Supprimer la pesée">
                    <span class="material-symbols-outlined" style="font-size:18px;" aria-hidden="true">delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  `
})
export class PeseeEchantillonComponent implements OnChanges {
  @Input() cible!: CibleTerrain;
  @Input() cibleId?: number | null;
  /** Unité proposée : g pour les poissons, kg pour la volaille et le bétail */
  @Input() uniteDefaut: 'g' | 'kg' = 'kg';
  /** Ouvre le formulaire dès l'affichage (bouton « Pesée » de la fiche) */
  @Input() ouvertAuDepart = false;
  @Output() enregistree = new EventEmitter<PeseeEchantillon>();

  readonly unites: ('g' | 'kg')[] = ['g', 'kg'];
  readonly id = 'pes-' + Math.random().toString(36).slice(2, 7);
  readonly peutSaisir = !RoleUtil.estComptable() && !RoleUtil.estContenu();
  readonly aujourdhui = this.jourLocal();

  liste: PeseeEchantillon[] = [];
  resultat: PeseeEchantillon | null = null;
  chargement = false;
  formulaire = false;
  envoi = false;
  erreur: string | null = null;
  mode: 'un' | 'total' = 'un';
  unite: 'g' | 'kg' = 'kg';
  date = this.aujourdhui;
  saisie: number | null = null;
  poids: number[] = [];
  poidsTotal: number | null = null;
  nombre: number | null = null;
  private clientRef = nouvelleClientRef();

  constructor(private service: ObservationsService, private fermeState: FermeStateService) {}

  ngOnChanges(): void {
    this.unite = this.uniteDefaut;
    this.charger();
    if (this.ouvertAuDepart && this.peutSaisir && !this.formulaire) { this.ouvrir(); }
  }

  charger(): void {
    if (!this.cible || !this.cibleId) { return; }
    this.chargement = true;
    this.service.pesees(this.cible, this.cibleId).subscribe({
      next: l => { this.liste = l || []; this.chargement = false; },
      error: () => { this.liste = []; this.chargement = false; }
    });
  }

  get stats(): { moyenne: number; min: number; max: number; ecart: number } {
    const l = this.poids, n = l.length;
    const moyenne = l.reduce((a, b) => a + b, 0) / n;
    const ecart = n > 1 ? Math.sqrt(l.reduce((a, b) => a + (b - moyenne) ** 2, 0) / (n - 1)) : 0;
    return { moyenne, min: Math.min(...l), max: Math.max(...l), ecart };
  }

  /** Grammes affichés en g sous 1 kg, sinon en kg */
  g(v?: number | null): string {
    if (v == null) { return '—'; }
    const a = Math.abs(v);
    return a >= 1000 ? `${(v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} kg` : `${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} g`;
  }

  ouvrir(): void {
    this.formulaire = true;
    this.resultat = null; this.erreur = null; this.poids = []; this.saisie = null; this.poidsTotal = null; this.nombre = null;
    this.date = this.jourLocal(); this.unite = this.uniteDefaut; this.mode = 'un';
    this.clientRef = nouvelleClientRef();
  }

  ajouterPoids(): void {
    const v = Number(this.saisie);
    if (!this.saisie || !(v > 0)) { return; }
    this.poids.push(v);
    this.saisie = null;
  }

  enregistrer(): void {
    this.erreur = null;
    if (this.mode === 'un' && this.saisie) { this.ajouterPoids(); }
    const corps: PeseeEchantillon = { clientRef: this.clientRef, fermeId: this.fermeState.fermeId, date: this.date, unite: this.unite };
    if (this.mode === 'un') {
      if (!this.poids.length) { this.erreur = 'Saisissez au moins un poids.'; return; }
      corps.poids = [...this.poids];
    } else {
      if (!(Number(this.poidsTotal) > 0) || !(Number(this.nombre) >= 1)) { this.erreur = 'Indiquez le poids total et le nombre de sujets pesés.'; return; }
      corps.poidsTotal = Number(this.poidsTotal); corps.nombre = Math.round(Number(this.nombre));
    }
    this.envoi = true;
    this.service.ajouterPesee(this.cible, this.cibleId!, corps).subscribe({
      next: p => {
        this.envoi = false; this.formulaire = false; this.resultat = p;
        this.liste = [p, ...this.liste.filter(x => x.id !== p.id)];
        this.enregistree.emit(p);
      },
      error: e => { this.envoi = false; this.erreur = e?.error?.message || 'La pesée n\'a pas pu être enregistrée. Réessayez.'; }
    });
  }

  peutSupprimer(p: PeseeEchantillon): boolean {
    return this.peutSaisir && RoleUtil.peutModifierSaisieModule({ createdBy: p.createdBy, createdAt: p.createdAt });
  }

  supprimer(p: PeseeEchantillon): void {
    Swal.fire({ title: 'Supprimer cette pesée ?', text: 'Ses effets (pesée de la cohorte, poids moyen du suivi du bassin) sont annulés.',
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc2626', confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler' }).then(r => {
      if (!r.isConfirmed) { return; }
      this.service.supprimerPesee(this.cible, p.id!).subscribe({
        next: () => { this.liste = this.liste.filter(x => x.id !== p.id); if (this.resultat?.id === p.id) { this.resultat = null; } this.enregistree.emit(p); },
        error: e => Swal.fire({ icon: 'error', title: 'Suppression impossible', text: e?.error?.message || 'Réessayez plus tard.' })
      });
    });
  }

  private jourLocal(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
